import { useHomework, type Homework } from "@/hooks/useHomework";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { BookOpen, Clock, CalendarDays, ChevronDown, ChevronUp, Check, Plus, Video, Camera, Mic, Trash2, Archive, Circle, CheckCircle2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useMemo, useRef, useCallback } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { formatChildFriendlyDate } from "@/lib/family-date";
import { differenceInCalendarDays, isSameWeek, format, parseISO, startOfISOWeek, addDays } from "date-fns";
import { nb } from "date-fns/locale";
import { cn } from "@/lib/utils";

/** Keywords indicating preparation for next week → deadline Sunday instead of Friday */
const PREP_KEYWORDS = ["pakkeliste", "forbered", "ta med", "husk å ta med", "gjør klar", "klargjør", "finn frem"];

/** Keywords indicating a submission/delivery deadline → always Friday */
const SUBMISSION_KEYWORDS = ["teams", "lever", "send inn", "innlevering", "last opp"];

function isPreparationTask(hw: Homework): boolean {
  // Check all text including subtasks for submission indicators
  const allText = [
    hw.title,
    hw.original_text || "",
    ...hw.homework_subtasks.map((st) => st.title),
  ].join(" ").toLowerCase();

  // If any subtask mentions submission/delivery, it's a Friday task regardless
  if (SUBMISSION_KEYWORDS.some((kw) => allText.includes(kw))) return false;

  // flexible_week scope is the primary signal for Sunday prep tasks
  if (hw.scope === "flexible_week") return true;

  // For week_due, only check prep keywords in title/original_text
  const titleText = (hw.title + " " + (hw.original_text || "")).toLowerCase();
  return PREP_KEYWORDS.some((kw) => titleText.includes(kw));
}

/** For week_due / flexible_week scope, compute Friday (or Sunday for prep tasks) of that ISO week */
function getEffectiveDeadline(hw: Homework): string | null {
  if (hw.due_date) return hw.due_date;
  if (hw.scope === "week_due" || hw.scope === "flexible_week") {
    const year = new Date().getFullYear();
    const jan4 = new Date(year, 0, 4);
    const startOfWeek1 = startOfISOWeek(jan4);
    const monday = addDays(startOfWeek1, (hw.week_number - 1) * 7);
    // Prep tasks (packing lists etc.) get Sunday deadline, others Friday
    const deadlineDay = isPreparationTask(hw) ? 6 : 4;
    const deadline = addDays(monday, deadlineDay);
    return format(deadline, "yyyy-MM-dd");
  }
  return null;
}

function smartDeadlineLabel(dateStr: string): { label: string; color: string } {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const d = parseISO(dateStr);
  const offset = differenceInCalendarDays(d, now);
  if (offset < 0) return { label: `Frist utløpt (${format(d, "d. MMM", { locale: nb })})`, color: "text-destructive" };
  if (offset === 0) return { label: "Frist i dag", color: "text-destructive" };
  if (offset === 1) return { label: "Frist i morgen", color: "text-amber-500 dark:text-amber-400" };
  if (isSameWeek(d, now, { weekStartsOn: 1 })) return { label: `Frist ${format(d, "EEEE", { locale: nb })}`, color: "text-muted-foreground" };
  return { label: `Frist ${format(d, "d. MMM", { locale: nb })}`, color: "text-muted-foreground" };
}

const SWIPE_THRESHOLD = 80;

/** Swipeable wrapper for parent homework cards */
function SwipeableHomeworkCard({
  children,
  onToggleDone,
  onDelete,
  isDone,
}: {
  children: React.ReactNode;
  onToggleDone: () => void;
  onDelete: () => void;
  isDone: boolean;
}) {
  const startXRef = useRef(0);
  const currentXRef = useRef(0);
  const [offsetX, setOffsetX] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const isDragging = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX;
    currentXRef.current = 0;
    isDragging.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const delta = e.touches[0].clientX - startXRef.current;
    currentXRef.current = delta;
    if (Math.abs(delta) > 10) isDragging.current = true;
    if (revealed) {
      const newOffset = Math.min(0, -140 + delta);
      setOffsetX(newOffset);
    } else {
      const clamped = Math.min(0, delta);
      setOffsetX(clamped);
    }
  }, [revealed]);

  const handleTouchEnd = useCallback(() => {
    const delta = currentXRef.current;
    if (revealed) {
      if (delta > 40) {
        setRevealed(false);
        setOffsetX(0);
      } else {
        setOffsetX(-140);
      }
    } else {
      if (delta < -SWIPE_THRESHOLD) {
        setRevealed(true);
        setOffsetX(-140);
      } else {
        setOffsetX(0);
      }
    }
    setTimeout(() => { isDragging.current = false; }, 0);
  }, [revealed]);

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* Background actions */}
      <div className="absolute inset-y-0 right-0 flex h-full">
        <button
          className="flex w-[70px] items-center justify-center bg-primary text-primary-foreground"
          onClick={onToggleDone}
        >
          <div className="flex flex-col items-center gap-0.5">
            {isDone ? <Archive className="h-4 w-4" /> : <Check className="h-4 w-4" />}
            <span className="text-[10px] font-medium">{isDone ? "Nullstill" : "Ferdig"}</span>
          </div>
        </button>
        <button
          className="flex w-[70px] items-center justify-center bg-destructive text-destructive-foreground"
          onClick={onDelete}
        >
          <div className="flex flex-col items-center gap-0.5">
            <Trash2 className="h-4 w-4" />
            <span className="text-[10px] font-medium">Slett</span>
          </div>
        </button>
      </div>

      {/* Foreground card */}
      <div
        className="relative bg-background transition-transform duration-200 ease-out"
        style={{ transform: `translateX(${offsetX}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
    </div>
  );
}

interface Props {
  childId: string;
  weekNumber?: number;
  isParent?: boolean;
  /** If true, subtask toggles are enabled */
  interactive?: boolean;
  /** Called when parent taps the + button to add homework (legacy single action) */
  onAdd?: () => void;
  /** Called when parent taps "Legg til lekse" – manual entry (alltid tilgjengelig) */
  onAddManual?: () => void;
  /** Called when parent taps "Importer fra ukeplan" – kun vist når canImport */
  onImport?: () => void;
  /** Om bruker har Pluss/Sammen for dette barnet – vis da import-knapp */
  canImport?: boolean;
  /** Device hashes for child device access (bypasses RLS via RPCs) */
  deviceHashes?: import("@/hooks/useDeviceHashes").DeviceHashes | null;
  /** Når true (f.eks. parent med sticky header): ikke vis seksjonsheader */
  hideHeader?: boolean;
  /** Når true: header rendres men er usynlig (beholder plass, unngår flicker ved scroll) */
  headerInvisible?: boolean;
}

const SCOPE_LABELS: Record<string, string> = {
  week_due: "Frist denne uken",
  daily: "Hver dag",
  flexible_week: "I løpet av uken",
};

const SUBJECT_EMOJI: Record<string, string> = {
  lesing: "📖",
  matte: "🔢",
  norsk: "✏️",
  engelsk: "🇬🇧",
  naturfag: "🔬",
  samfunnsfag: "🌍",
  krle: "🕊️",
  musikk: "🎵",
  kunst: "🎨",
  gym: "🏃",
  praktisk: "🎒",
  annet: "📋",
};

const SUBJECT_LABELS: Record<string, string> = {
  lesing: "Lesing",
  matte: "Matte",
  norsk: "Norsk",
  engelsk: "Engelsk",
  naturfag: "Naturfag",
  samfunnsfag: "Samfunnsfag",
  krle: "KRLE",
  musikk: "Musikk",
  kunst: "Kunst",
  gym: "Gym",
  praktisk: "Praktisk",
  annet: "Annet",
};

export function HomeworkSection({ childId, weekNumber, isParent = false, interactive = true, onAdd, onAddManual, onImport, canImport, deviceHashes, hideHeader = false, headerInvisible = false }: Props) {
  const { items: rawItems, isLoading, toggleSubtask, deleteHomework, archiveHomework, unarchiveHomework, completionStats } = useHomework(childId, weekNumber, deviceHashes);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  // Sort by effective deadline ascending (earliest first), nulls last
  // Filter out homework from past weeks (deadline already passed and completed, or deadline > 7 days ago)
  const items = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return [...rawItems]
      .filter((hw) => {
        const deadline = getEffectiveDeadline(hw);
        if (!deadline) return true; // no deadline → always show
        const d = parseISO(deadline);
        const daysAgo = differenceInCalendarDays(now, d);
        // Show if: not yet due, or due within last 2 days (grace period for late completion)
        if (daysAgo <= 2) return true;
        // Past deadline > 2 days: only show if still incomplete
        return !hw.completed_at;
      })
      .sort((a, b) => {
        const da = getEffectiveDeadline(a);
        const db = getEffectiveDeadline(b);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da.localeCompare(db);
      });
  }, [rawItems]);

  if (isLoading || items.length === 0) return null;

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Child-friendly UI (11–13 MVP: same visual grammar as "I dag vil jeg få til")
  if (!isParent) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-display font-bold flex items-center gap-2">
          Lekser
          <Badge variant="secondary" className="text-xs ml-2">{items.length}</Badge>
        </h2>

        <div className="space-y-2">
          {items.map((hw) => {
            const stats = completionStats.find((s) => s.homeworkId === hw.id);
            const isDone = !!hw.completed_at;
            const totalMinutes = hw.homework_subtasks.reduce((sum, st) => sum + (st.estimated_minutes || 0), 0);
            const isExpanded = expandedIds.has(hw.id);
            const hasSubtasks = hw.homework_subtasks.length > 0;

            return (
              <Card
                key={hw.id}
                className={cn(
                  "transition-all border-0 shadow-sm rounded-2xl",
                  isDone ? "bg-[hsl(var(--success))]/5" : "hover:shadow-md",
                )}
              >
                <CardContent className="p-4">
                  <div
                    className="flex items-start gap-3 cursor-pointer active:bg-muted/40 rounded-xl -m-1 p-1 min-h-[44px]"
                    onClick={() => hasSubtasks && toggleExpanded(hw.id)}
                  >
                    <div className="shrink-0 mt-0.5">
                      <BookOpen className="h-6 w-6 text-muted-foreground" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold leading-snug ${isDone ? "line-through text-muted-foreground" : ""}`}>
                        {hw.title}
                      </p>

                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                        {(() => {
                          const deadline = getEffectiveDeadline(hw);
                          const deadlineLabel = !deadline
                            ? SCOPE_LABELS[hw.scope]
                            : smartDeadlineLabel(deadline).label;
                          return (
                            <>
                              {deadlineLabel}
                              {totalMinutes > 0 && (
                                <><span className="text-muted-foreground/70">·</span> ~{totalMinutes} min</>
                              )}
                            </>
                          );
                        })()}
                      </p>

                      {stats && stats.total > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <Progress value={stats.percentage} className="h-2 flex-1 bg-muted/60" />
                          <span className="text-xs text-muted-foreground shrink-0">
                            {stats.done}/{stats.total}
                          </span>
                        </div>
                      )}
                    </div>

                    {hasSubtasks && (
                      <div className="shrink-0 mt-1 text-muted-foreground">
                        {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </div>
                    )}
                  </div>

                  {isExpanded && hasSubtasks && (
                    <div className="mt-3 space-y-3">
                      {hw.homework_subtasks.map((st) => (
                        <div
                          key={st.id}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (interactive) {
                              toggleSubtask.mutate({ subtaskId: st.id, completed: !st.completed });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              if (interactive) {
                                toggleSubtask.mutate({ subtaskId: st.id, completed: !st.completed });
                              }
                            }
                          }}
                          className="flex items-start gap-3 cursor-pointer active:bg-muted/30 rounded-lg -mx-1 px-2 py-1.5 min-h-[44px]"
                        >
                          <div className="shrink-0 mt-0.5">
                            {st.completed ? (
                              <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))]" />
                            ) : (
                              <Circle className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              "text-sm font-medium leading-snug line-clamp-2",
                              st.completed && "line-through text-muted-foreground",
                            )}>
                              {st.title}
                            </p>
                            {(st.estimated_minutes || st.requires_upload) && (
                              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                                {st.estimated_minutes != null && st.estimated_minutes > 0 && (
                                  <span>{st.estimated_minutes} min</span>
                                )}
                                {st.requires_upload && (
                                  <Badge variant="secondary" className="text-[11px] px-2 py-0.5 rounded-full font-normal">
                                    {st.requires_upload === "video" ? "Video" : st.requires_upload === "image" ? "Bilde" : "Lyd"}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Parent UI (unchanged)
  return (
    <div className="space-y-3">
      {!hideHeader && !headerInvisible && (
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-display font-semibold flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Lekser
            <Badge variant="secondary" className="text-xs">{items.length}</Badge>
          </h2>
          {(onAddManual ?? onAdd) && (
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" className="rounded-full text-xs gap-1 border-primary/40" onClick={onAddManual ?? onAdd}>
                <Plus className="h-3.5 w-3.5 text-primary" /> Legg til lekse
              </Button>
              {onImport && canImport && (
                <Button variant="outline" size="sm" className="rounded-full text-xs gap-1" onClick={onImport}>
                  <Upload className="h-3.5 w-3.5 text-muted-foreground" /> Importer
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {items.map((hw) => {
          const stats = completionStats.find((s) => s.homeworkId === hw.id);
          const isDone = !!hw.completed_at;
          const totalMinutes = hw.homework_subtasks.reduce((sum, st) => sum + (st.estimated_minutes || 0), 0);
          const isExpanded = expandedIds.has(hw.id);
          const hasSubtasks = hw.homework_subtasks.length > 0;

          return (
            <SwipeableHomeworkCard
              key={hw.id}
              isDone={isDone}
              onToggleDone={() => isDone ? unarchiveHomework.mutate(hw.id) : archiveHomework.mutate(hw.id)}
              onDelete={() => setDeleteTarget(hw.id)}
            >
              <Card className={`transition-all ${isDone ? "opacity-60" : ""}`}>
                <CardContent className="p-3">
                  {/* Header row – tap to expand/collapse */}
                  <div
                    className="flex items-start gap-3 cursor-pointer active:bg-muted/60 rounded-md -m-1 p-1"
                    onClick={() => hasSubtasks && toggleExpanded(hw.id)}
                  >
                    <div className="mt-0.5 shrink-0">
                      {isDone ? (
                        <div className="h-6 w-6 rounded-lg bg-primary flex items-center justify-center">
                          <Check className="h-3.5 w-3.5 text-primary-foreground" />
                        </div>
                      ) : (
                        <div className="h-6 w-6 rounded-lg bg-secondary flex items-center justify-center">
                          <BookOpen className="h-3.5 w-3.5 text-primary" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${isDone ? "line-through text-muted-foreground" : ""}`}>
                        {hw.title}
                      </p>

                      <div className="text-xs text-muted-foreground mt-1">
                        {hw.subject && <span>{hw.subject}</span>}
                        {hw.subject && getEffectiveDeadline(hw) && <span> · </span>}
                        {getEffectiveDeadline(hw) && (
                          <span className={smartDeadlineLabel(getEffectiveDeadline(hw)!).color}>
                            {smartDeadlineLabel(getEffectiveDeadline(hw)!).label}
                          </span>
                        )}
                        {(hw.subject || getEffectiveDeadline(hw)) && totalMinutes > 0 && <span> · </span>}
                        {totalMinutes > 0 && <span>~{totalMinutes} min</span>}
                      </div>

                      {/* Progress bar */}
                      {stats && stats.total > 0 && (
                        <div className="mt-2">
                          <Progress value={stats.percentage} className="h-1 flex-1" />
                        </div>
                      )}
                    </div>

                    {hasSubtasks && (
                      <div className="shrink-0 mt-0.5">
                        {isExpanded
                          ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        }
                      </div>
                    )}
                  </div>

                  {/* Inline subtasks */}
                  {isExpanded && hasSubtasks && (
                    <div className="mt-3 ml-9 space-y-1.5 border-l-2 border-muted pl-3">
                      {hw.homework_subtasks.map((st) => (
                        <div key={st.id} className="flex items-start gap-2">
                          <Checkbox
                            checked={st.completed}
                            onCheckedChange={(checked) =>
                              toggleSubtask.mutate({ subtaskId: st.id, completed: !!checked })
                            }
                            className="mt-0.5"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm ${st.completed ? "line-through text-muted-foreground" : ""}`}>
                              {st.title}
                            </span>
                            {(st.estimated_minutes || st.requires_upload) && (
                              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                {st.estimated_minutes ? (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-0.5 font-normal">
                                    <Clock className="h-2.5 w-2.5" /> {st.estimated_minutes} min
                                  </Badge>
                                ) : null}
                                {st.requires_upload && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-0.5 font-normal border-primary/30 text-primary">
                                    {st.requires_upload === "video" && <Video className="h-2.5 w-2.5" />}
                                    {st.requires_upload === "image" && <Camera className="h-2.5 w-2.5" />}
                                    {st.requires_upload === "audio" && <Mic className="h-2.5 w-2.5" />}
                                    {st.requires_upload}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </SwipeableHomeworkCard>
          );
        })}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette lekse?</AlertDialogTitle>
            <AlertDialogDescription>
              Leksen og alle deloppgaver fjernes permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) {
                  deleteHomework.mutate(deleteTarget);
                  setDeleteTarget(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
